'use client';
import { useState } from 'react';
import Link from 'next/link';
import { ShieldCheck, Target, RefreshCw, Layers, ListChecks, Workflow, LineChart, ChevronRight, Shield, Award, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Breadcrumbs from '@/components/Breadcrumbs';
import CTASection from '@/components/CTASection';
import HeroEntrance from '@/components/HeroEntrance';
import ScrollReveal from '@/components/ScrollReveal';

const breadcrumbItems = [
  { label: 'Services', href: '/services' },
  { label: 'Security', href: '/services/small-business-cybersecurity' },
  { label: 'Penetration Testing' }
];

const trustBadgeItems = ['SOC 2', 'ISO 27001', 'HIPAA', 'PCI DSS', 'NIST CSF'];

const heroHighlights = [
  { icon: ShieldCheck, label: 'CISSP-led manual validation on every critical finding' },
  { icon: Target, label: 'Prioritized remediation aligned to business risk' },
  { icon: RefreshCw, label: 'Continuous validation to stay ahead of drift' }
];

const storyBlocks: Array<{
  id: string;
  label: string;
  title: string;
  summary: string;
  bullets: string[];
  icon: LucideIcon;
}> = [
  {
    id: 'story-programs',
    label: '🧪 Testing',
    title: 'Start with Testing Programs',
    summary: 'Launch with the testing cadence that matches your risk, budget, and operating tempo.',
    bullets: [
      'Select vulnerability scans, full penetration tests, or quarterly validation in minutes.',
      'Blend internal, external, web app, and cloud scope without reworking contracts.',
      'Extend coverage instantly with 250-IP add-on blocks as environments grow.'
    ],
    icon: Layers
  },
  {
    id: 'story-validation',
    label: '✅ Validation',
    title: 'Validate with Human-Reviewed Findings',
    summary: 'Pair automation with ethical hackers so every critical vulnerability is verified and reproducible.',
    bullets: [
      'Manual exploitation confirms business impact before it ever reaches your team.',
      'Reports include screenshots, payloads, and fix guidance for each high-risk path.',
      'Risk scoring aligns likelihood, impact, and existing control strength.'
    ],
    icon: ListChecks
  },
  {
    id: 'story-vciso',
    label: '🧩 vCISO',
    title: 'Integrate vCISO Coordination',
    summary: 'Keep security leadership in the loop so remediation maps cleanly to governance and compliance.',
    bullets: [
      'Your vCISO prioritizes fixes against HIPAA, SOC 2, ISO 27001, PCI, and NIST CSF.',
      'Findings flow into roadmaps, Jira boards, or GRC tools without manual rework.',
      'Remediation checkpoints turn ad-hoc testing into a managed security program.'
    ],
    icon: Workflow
  },
  {
    id: 'story-visibility',
    label: '📈 Visibility',
    title: 'Achieve Executive Visibility',
    summary: 'Give leadership clear evidence of risk reduction, compliance progress, and next best actions.',
    bullets: [
      'Executive summaries translate technical findings into board-ready language.',
      'Heat maps and trend lines show dwell time, remediation velocity, and residual risk.',
      '30-day action plans keep owners, dates, and funding aligned with confidence.'
    ],
    icon: LineChart
  }
];

const testingPlans = [
  {
    name: 'Vulnerability Scan',
    cadence: 'One-Time',
    price: '$5,000',
    description: 'Automated internal and external vulnerability scan using open-source tools. Includes prioritized findings, remediation recommendations, and one executive report.',
    idealFor: 'Businesses needing a fast, affordable baseline security check.',
    highlights: [
      'Internal and external scan of up to 250 IPs',
      'Open-source vulnerability tools plus manual verification',
      'Risk scoring and prioritization',
      'Executive summary report with remediation guidance'
    ]
  },
  {
    name: 'Penetration Test',
    cadence: 'One-Time',
    price: '$10,000',
    description: 'Comprehensive internal and external penetration test for up to 250 IPs. Combines automated scanning with manual validation and exploitation.',
    idealFor: 'Organizations preparing for compliance certifications (SOC 2, ISO 27001, HIPAA, PCI).',
    highlights: [
      'Manual and automated testing of 250 IPs',
      'External perimeter and internal network analysis',
      'Vulnerability exploitation and privilege escalation testing',
      'Executive and technical reports with visual proof of findings',
      'Findings review session with a security engineer'
    ],
    featured: true
  },
  {
    name: 'Continuous Testing',
    cadence: 'Quarterly',
    price: '$3,000 / month',
    description: 'Quarterly penetration testing with automated vulnerability scanning, remediation validation, and executive reporting.',
    idealFor: 'Companies needing year-round coverage, reporting, and measurable improvement.',
    highlights: [
      'Quarterly penetration testing and scanning',
      'Continuous monitoring of known vulnerabilities',
      'Monthly trend and progress reporting',
      'Quarterly executive summaries',
      'Priority retesting of remediated issues'
    ]
  }
];

const processSteps = [
  {
    title: 'Discovery & Scoping',
    description: 'Define IP ranges, assets, objectives, and environment to right-size the engagement.',
    icon: Target
  },
  {
    title: 'Assessment Execution',
    description: 'Run automated scanning and manual testing across internal, external, cloud, and application layers.',
    icon: Shield
  },
  {
    title: 'Analysis & Prioritization',
    description: 'Correlate findings, validate exploitation paths, and rank vulnerabilities by business risk.',
    icon: ListChecks
  },
  {
    title: 'Reporting & Review',
    description: 'Deliver executive and technical reports, heat maps, and walkthrough sessions with your team.',
    icon: LineChart
  },
  {
    title: 'Remediation Validation',
    description: 'Optional retesting to verify fixes, update metrics, and close the loop with leadership.',
    icon: CheckCircle2
  }
];

const deliverables = [
  { title: 'Executive Summary (PDF)', icon: Award },
  { title: 'Technical Findings Report (PDF or CSV)', icon: ListChecks },
  { title: 'Vulnerability Heat Map and Risk Score', icon: LineChart },
  { title: '30-Day Remediation Plan', icon: Target },
  { title: 'Quarterly Trend Report (for continuous plans)', icon: RefreshCw }
];

const faqs = [
  {
    question: 'What exactly is penetration testing?',
    answer: "Penetration testing is a simulated cyberattack on your computer systems, networks, or applications to identify security vulnerabilities that could be exploited by real attackers. We use the same tools and techniques as malicious hackers to find weaknesses before they do, then provide detailed reports on how to fix them."
  },
  {
    question: 'How long does a penetration test take?',
    answer: "The timeline depends on your environment's size and complexity. Simple assessments typically take 1-2 weeks, while comprehensive assessments can take 3-4 weeks. We minimize disruption to your operations and can work around your schedule. You'll receive preliminary findings within days of starting."
  },
  {
    question: 'Will the penetration test disrupt our operations?',
    answer: 'Our testing is designed to be non-disruptive to your normal business operations. We coordinate with your IT team to schedule testing during low-impact periods and avoid any activities that could cause downtime or data loss.'
  },
  {
    question: "What's the difference between a vulnerability scan and a penetration test?",
    answer: 'A vulnerability scan is an automated tool that identifies known technical weaknesses. Our penetration testing goes much deeper – we actively attempt to exploit vulnerabilities, chain attacks together, and demonstrate real-world business impact. We provide context about which vulnerabilities matter most to your specific business.'
  },
  {
    question: 'How often should we conduct penetration tests?',
    answer: 'Best practice is to conduct a comprehensive penetration test annually, with quarterly assessments for high-risk environments. You should also test after major changes like new systems, mergers, or significant growth.'
  },
  {
    question: 'What happens after we receive the penetration test report?',
    answer: "We don't just hand you a report and disappear. We walk through the findings with your team, help prioritize remediation efforts, and provide guidance on fixing issues. Depending on your plan, we offer 30-90 days of follow-up support."
  },
  {
    question: 'Can you test our cloud environment (AWS, Azure, GCP)?',
    answer: 'Yes. Our comprehensive plans include cloud security testing covering infrastructure, applications, and configurations across all major cloud platforms including AWS, Azure, Google Cloud, and hybrid environments.'
  }
];

const whyChooseUs = [
  {
    icon: Award,
    title: 'Proven Expertise',
    description: 'Certified security professionals with decades of real-world experience across healthcare, finance, and professional services'
  },
  {
    icon: Target,
    title: 'Business-Focused Testing',
    description: 'We prioritize vulnerabilities based on your business context, not just CVSS scores'
  },
  {
    icon: Shield,
    title: 'Real-World Validation',
    description: 'Manual exploitation by ethical hackers ensures every finding is actionable and reproducible'
  }
];

export default function PenetrationTestingClient() {
  const [activeStory, setActiveStory] = useState<string | null>(storyBlocks[0]?.id ?? null);
  const activeStoryBlock = storyBlocks.find(block => block.id === activeStory) ?? null;
  const contactHref = (cta: string) => `/contact?ref=service-penetration-testing&cta=${cta}`;

  return (
    <>
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2813e8] via-[#1e40af] to-[#2cc3d1] text-white py-24 px-4 overflow-hidden">
          {/* Hero Background Image with 20% opacity and mix-blend-overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20 mix-blend-overlay"
            style={{ backgroundImage: "url('https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/hero-penetration-testing/width=1920,quality=80,format=auto')" }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#2813e8]/80 via-transparent to-[#2cc3d1]/60" />

          {/* Decorative blur effects */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-96 h-96 bg-white dark:bg-gray-800 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Main Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
                  <ShieldCheck className="h-4 w-4 text-cyan-200" />
                  <span className="text-sm font-semibold uppercase tracking-wider text-cyan-200">
                    Vulnerability Testing Programs
                  </span>
                </div>

                <HeroEntrance delay={0}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                  Vulnerability &amp; Penetration Testing
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 to-white mt-2">
                    for SMB Security Teams
                  </span>
                </h1>
                </HeroEntrance>

                <HeroEntrance delay={150}>
                <p className="text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed">
                  Identify, test, and continuously improve your security posture. Choose a one-time scan,
                  full penetration test, or continuous validation to keep evolving threats in check.
                </p>
                </HeroEntrance>

                <div className="flex flex-wrap gap-4">
                  <Link
                    href={contactHref('hero-primary')}
                    className="inline-flex items-center justify-center gap-2 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95"
                  >
                    Request a Testing Plan
                    <span className="text-xl">→</span>
                  </Link>
                  <Link
                    href="#plans"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white dark:hover:bg-gray-800/20 px-8 py-4 rounded-xl font-bold text-lg transition-all"
                  >
                    Compare Plans
                  </Link>
                </div>
              </div>

              {/* Right Column - Highlights Card */}
              <div className="bg-white/10 dark:bg-gray-800/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:bg-white dark:hover:bg-gray-800/15 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Built for Small &amp; Mid-Sized Businesses
                </h3>
                <ul className="space-y-4">
                  {heroHighlights.map(highlight => (
                    <li key={highlight.label} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-cyan-400/20 rounded-xl flex items-center justify-center">
                        <highlight.icon className="h-6 w-6 text-cyan-200" />
                      </div>
                      <span className="text-blue-100 text-lg leading-relaxed">{highlight.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-16 pt-8 border-t border-white/20">
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                <span className="text-sm font-semibold uppercase tracking-wider text-blue-200">
                  Validated Against
                </span>
                {trustBadgeItems.map(item => (
                  <div key={item} className="flex items-center gap-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <ShieldCheck className="h-4 w-4 text-cyan-200" />
                    <span className="font-semibold">{item}</span>
                  </div>
                ))}
                <span className="text-blue-200">and more</span>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        {/* Program Overview */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Identify, Test, and Continuously Improve
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed">
                Flexible testing programs designed for small and midsize businesses. Start with a one-time scan or move to quarterly penetration testing with continuous monitoring. Every engagement includes prioritized remediation guidance and executive-ready reporting.
              </p>
            </div>

            {/* Interactive Story Blocks */}
            <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-3xl p-8 md:p-12 shadow-lg">
              <div className="flex flex-wrap gap-3 mb-8">
                {storyBlocks.map(block => {
                  const Icon = block.icon;
                  const isActive = activeStory === block.id;
                  return (
                    <button
                      key={block.id}
                      type="button"
                      onClick={() => setActiveStory(isActive ? null : block.id)}
                      className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white shadow-lg scale-105'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:scale-105'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full ${
                        isActive ? 'bg-white/20 dark:bg-gray-800/20' : 'bg-white dark:bg-gray-800'
                      }`}>
                        <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
                      </div>
                      {block.label}
                    </button>
                  );
                })}
              </div>

              {activeStoryBlock && (
                <div className="transform transition-all duration-500 ease-out">
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                    {activeStoryBlock.summary}
                  </p>
                  <div className="grid md:grid-cols-2 gap-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8">
                    <div>
                      <div className="flex items-start gap-4 mb-6">
                        <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] rounded-2xl flex items-center justify-center">
                          <activeStoryBlock.icon className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeStoryBlock.title}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {activeStoryBlock.bullets.map(bullet => (
                        <div key={bullet} className="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                          <ChevronRight className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">{bullet}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section id="plans" className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Vulnerability &amp; Penetration Testing Pricing
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Pick the starting point that matches your current needs—then expand coverage as your program grows.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testingPlans.map((plan, index) => {
                const planCtaId = `pricing-${plan.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                return (
                  <ScrollReveal key={plan.name} animation="fade-up" delay={index * 100}>
                  <div
                    className={`relative h-full bg-white dark:bg-gray-800 border-2 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 ${
                      plan.featured ? 'border-blue-600 ring-4 ring-blue-100' : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {plan.featured && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="inline-block bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg">
                          ⭐ Most Popular
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{plan.name}</h3>
                      <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-4 py-1 rounded-full text-sm font-semibold">
                        {plan.cadence}
                      </span>
                    </div>
                    <p className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-4">{plan.price}</p>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">{plan.description}</p>
                    <div className="mb-6">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                        Ideal For
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{plan.idealFor}</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.highlights.map(item => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      href={contactHref(planCtaId)}
                      className={`block w-full text-center px-6 py-4 rounded-xl font-bold transition-all ${
                        plan.featured
                          ? 'bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white hover:shadow-lg transform hover:scale-105'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      Request Scoping Consult
                    </Link>
                  </div>
                  </ScrollReveal>
                );
              })}
            </div>

            <div className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 dark:border-green-800 rounded-2xl p-6">
              <p className="text-green-800 dark:text-green-300 text-center">
                <strong className="font-bold">Add-on IP Blocks:</strong> Expand testing coverage by 250 IPs for $2,000 each.
                <span className="block mt-2">Example: 500 IPs = $12,000 • 750 IPs = $14,000.</span>
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Continuous Testing */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="relative bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] rounded-3xl p-12 text-white overflow-hidden shadow-2xl">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white dark:bg-gray-800 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-300 rounded-full blur-3xl"></div>
              </div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Why Choose Continuous Testing?
                </h2>
                <p className="text-xl text-blue-100 mb-4 leading-relaxed max-w-4xl">
                  One-time tests reveal where you stand today — continuous testing ensures you stay secure tomorrow.
                  Regular validation reduces dwell time, detects configuration drift, and proves ongoing compliance.
                </p>
                <p className="text-2xl font-bold">
                  Save 10% compared to running one-time tests quarterly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Program Integration */}
        <section className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-3xl p-12 shadow-lg">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                How It Fits Into Your Security Program
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                Every assessment is coordinated by your vCISO, ensuring findings integrate into your broader risk
                management and compliance frameworks (HIPAA, SOC 2, NIST CSF, etc.). Results can be automatically
                surfaced in your Cynomi or Drata dashboards for continuous visibility and stakeholder updates.
              </p>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Engagement Process
              </h2>
            </div>
            <div className="grid md:grid-cols-5 gap-6">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <ScrollReveal key={step.title} animation="fade-up" delay={index * 100}>
                  <div
                    className="relative bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    <div className="absolute -top-5 left-6 flex items-center justify-center w-10 h-10 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] rounded-full text-white font-bold text-lg shadow-lg">
                      {index + 1}
                    </div>
                    <div className="mt-6">
                      <div className="flex items-center justify-center w-12 h-12 bg-blue-50 dark:bg-blue-950/40 rounded-xl mb-4">
                        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">{step.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Deliverables */}
        <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Deliverables
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {deliverables.map((item, index) => {
                const Icon = item.icon;
                return (
                  <ScrollReveal key={item.title} animation="fade-up" delay={index * 100}>
                  <div
                    className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:border-blue-500 transition-all duration-300 hover:scale-105"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl flex items-center justify-center">
                        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.title}</h3>
                      </div>
                    </div>
                  </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Why Choose Inventive HQ
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Enterprise-grade security testing without the enterprise price tag
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => {
                const Icon = item.icon;
                return (
                  <ScrollReveal key={item.title} animation="fade-up" delay={index * 100}>
                  <div
                    className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-xl hover:border-blue-500 transition-all duration-300 hover:scale-105"
                  >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] rounded-2xl mb-6">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{item.description}</p>
                  </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="max-w-4xl mx-auto space-y-4">
              {faqs.map(faq => (
                <details
                  key={faq.question}
                  className="group bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <summary className="flex items-center justify-between cursor-pointer text-xl font-bold text-gray-900 dark:text-gray-100 list-none">
                    {faq.question}
                    <ChevronRight className="h-6 w-6 text-blue-600 dark:text-blue-400 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-4 text-gray-600 dark:text-gray-400 leading-relaxed pl-1">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection
          title="Ready to Validate Your Defenses?"
          description="Protect your business, meet compliance requirements, and gain peace of mind knowing your vulnerabilities are identified and remediated before attackers find them."
          primaryButton={{ text: "Talk to an Expert", href: contactHref('cta-final') }}
          secondaryButton={{ text: "View All Security Services", href: "/services/small-business-cybersecurity" }}
        />
      </main>
    </>
  );
}
